import React, {useCallback, useMemo} from 'react';
import {type SxProps} from '@mui/material';
import {Box} from '@mui/system';
import assert from 'assert-ts';
import {useNavigate} from 'react-router';
import {type CollectionSubType, Concept} from 'types';
import type {Data} from 'schemaDefinition/types';
import type {Collection} from 'services/series/types';
import {ColorPalette} from 'theme';
import {useCodelists} from 'services/codelists';
import {getActualDataChangesWithSchema} from 'services/metadata/functions/getActualDataChangesWithSchema';
import {
  useGetSeriesQuery,
  useMergeSeriesMutation,
} from 'services/series/seriesApi';
import {assertCollectionValue} from 'services/utils';
import {isCollectionValue} from 'services/utils/functions/isCollectionValue';
import {mapRtkErrorToHttpError} from 'store/api/mapRtkErrorToHttpError';
import {ActionButton, Card, FlexBox, Text, useSnacks} from 'components';
import {RtkContent} from 'components/rtkContent/RtkContent';
import {DataFormV1} from 'schema';
import {DataFormCompareLayout} from 'schema/form/components/DataFormCompareLayout';
import {validate} from 'schema/form/functions/validators';
import {useSchema} from 'schemas';
import {useCollectionFormConfiguration} from 'editors/collections/hooks/useCollectionFormConfiguration';
import {useCollectionsCodelistIds} from '../hooks';

const primaryColorSx: SxProps = {
  color: ColorPalette.primary.burgundy,
};

type Props = {
  seriesId: string;
  otherId: string;
};
export const CompareSeries: React.FC<Props> = ({seriesId, otherId}) => {
  const navigate = useNavigate();
  const codelistIds = useCollectionsCodelistIds();
  const codelistMap = useCodelists(codelistIds);
  const {errorSnack, warningSnack, successSnack} = useSnacks();
  const {
    data: seriesData,
    status: seriesStatus,
    error: seriesError,
  } = useGetSeriesQuery({
    id: seriesId,
  });
  const {
    data: otherData,
    status: otherStatus,
    error: otherError,
  } = useGetSeriesQuery({
    id: otherId,
  });
  const [mergeSeries] = useMergeSeriesMutation();

  const formConfiguration = useCollectionFormConfiguration();

  const collectionSubtype: CollectionSubType =
    seriesData?.collectionType ?? Concept.series;

  const schema = useSchema({
    entity: Concept.collection,
    subType: collectionSubtype,
  });

  const {actualChanges, trimmedSchema} = useMemo(() => {
    const changes =
      seriesData && otherData
        ? getActualDataChangesWithSchema(
            otherData,
            seriesData,
            schema,
            undefined,
            {
              excludeEmpty: true,
              excludeEqual: true,
              excludeReadOnly: false,
            },
          )
        : undefined;

    return {
      ...changes,
    };
  }, [otherData, seriesData, schema]);

  const [editedData, setEditedData] = React.useState<Collection | undefined>(
    seriesData,
  );
  const onDataChanged = useCallback((data: unknown) => {
    if (isCollectionValue(data)) {
      setEditedData(data);
    } else {
      setEditedData(undefined);
    }
  }, []);

  const handleSave = useCallback((): Promise<void> => {
    const validation = validate(
      editedData as Data,
      undefined,
      schema,
      assert(codelistMap, 'CompareSeries: codelistMap is required'),
    );

    if (validation.valid !== 'valid') {
      if (validation.valid === 'warning') {
        warningSnack(validation.errors.map(e => e.value).join(', '));
      } else {
        if (validation.errors.filter(e => e.value).length > 0) {
          errorSnack(validation.errors.map(e => e.value).join(', '));
        }
        return Promise.resolve();
      }
    }

    return mergeSeries({
      target: assertCollectionValue(editedData),
      source: assertCollectionValue(otherData),
    })
      .unwrap()
      .then(() => {
        successSnack('Seriene ble slått sammen');
        navigate('overview');
        return Promise.resolve();
      })
      .catch(error => {
        errorSnack('En feil oppstod!', mapRtkErrorToHttpError(error));
      });
  }, [
    editedData,
    schema,
    codelistMap,
    mergeSeries,
    otherData,
    warningSnack,
    errorSnack,
    successSnack,
    navigate,
  ]);

  return (
    <>
      <FlexBox horizontal right width={'100%'}>
        <ActionButton title={'Slå sammen serier'} onClick={handleSave} />
      </FlexBox>
      <FlexBox horizontal pb={1} px={2} flex={1}>
        <DataFormCompareLayout
          renderCurrent={() => (
            <>
              <Text variant="h4" sx={primaryColorSx} textTransform="uppercase">
                Serie som beholdes
              </Text>
              <Text variant="caption" sx={primaryColorSx}>
                Denne serien vil få alle koblingene, og eventuelle endringer som
                gjøres.
              </Text>
            </>
          )}
          renderProposed={() => (
            <Box ml={2}>
              <Text variant="h4" sx={primaryColorSx} textTransform="uppercase">
                Serie som slettes
              </Text>
              <Text variant="caption" sx={primaryColorSx}>
                Koblingene vil bli flyttet fra denne serien til den andre
                serien, og denne serien vil bli slettet etterpå.
              </Text>
            </Box>
          )}
        />
      </FlexBox>
      <FlexBox horizontal pb={1} px={2} flex={1}>
        <DataFormCompareLayout
          renderCurrent={() => (
            <RtkContent status={seriesStatus} error={seriesError}>
              <DataFormV1
                id={'mergedSeriesData'}
                schema={schema}
                originalData={seriesData}
                data={seriesData ?? {}}
                configuration={formConfiguration}
                mode="edit"
                relatedData={{}}
                showErrors={true}
                onDataChanged={onDataChanged}
              />
            </RtkContent>
          )}
          renderProposed={() => (
            <RtkContent status={otherStatus} error={otherError}>
              {trimmedSchema ? (
                <DataFormV1
                  id={'mergedSeriesData'}
                  schema={trimmedSchema}
                  data={actualChanges ?? {}}
                  configuration={formConfiguration}
                  mode="read-only"
                  relatedData={{}}
                />
              ) : (
                <Card>Ingen forskjeller</Card>
              )}
            </RtkContent>
          )}
        />
      </FlexBox>
    </>
  );
};
